import type Luminary from '@lumen5/luminary';
import type { AudioSourceData, MediaSourceData } from '@lumen5/luminary';

import type { LanguageEnum, TranscriptWord } from 'js/src/client';
import {
    applyPlaybackRegionsToVoiceDrivenSceneGroupOperation,
    buildLuminaryInstanceOperation,
} from 'js/src/data/clip-creator/clip-creator-operations';
import { MediaRegistry } from 'js/src/agent/registries/MediaRegistry';
import { VoiceoverRegistry } from 'js/src/agent/registries/VoiceoverRegistry';
import {
    buildAudioSourceData,
    createEmptyClipLuminaryInstance,
} from 'js/src/data/clip-creator/clip-creator-utils';
import {
    convertBackgroundAssetToFlattenedAsset,
    flattenedAssetIsVideo,
    isFlattenedAssetMediaSourceDataCompatible,
    slideBackgroundModelIsVideoBackgroundModel,
} from 'js/src/data/media';
import type { AppDispatch, RootState } from 'js/src/libs/RootState';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';

export type ClipPreviewState =
    | { status: 'ready'; luminary: Luminary }
    | { status: 'unmatched' }
    | { status: 'failed' };

type BuildClipPreviewOptions = {
    templateId: string;
    aspectRatio: Luminary['aspectRatio'];
    sourceData: MediaSourceData | AudioSourceData;
    transcriptWords: TranscriptWord[];
    transcriptLanguage: LanguageEnum;
    startTimeMs: number;
    endTimeMs: number;
};

export function buildClipPreviewLuminary(options: BuildClipPreviewOptions) {
    return async (dispatch: AppDispatch, getState: () => RootState): Promise<Luminary> => {
        const preview = createEmptyClipLuminaryInstance('clip-select-preview');
        try {
            const callable = makeOperationsCallable(
                {
                    buildLuminaryInstanceOperation,
                    applyPlaybackRegionsToVoiceDrivenSceneGroupOperation,
                },
                preview,
                dispatch,
                getState,
            );
            await callable.buildLuminaryInstance({
                templateId: options.templateId,
                sourceData: options.sourceData,
                transcriptWords: options.transcriptWords,
                transcriptLanguage: options.transcriptLanguage,
                aspectRatio: options.aspectRatio,
            });
            callable.applyPlaybackRegionsToVoiceDrivenSceneGroup([
                [options.startTimeMs, options.endTimeMs],
            ]);
            // Warm the buffer at the clip's offset so the first play doesn't stall.
            preview.seek(0).catch(() => {});
            return preview;
        } catch (error) {
            preview.destroy();
            throw error;
        }
    };
}

export function resolveClipSource(
    mediaId: string,
): { sourceData: MediaSourceData | AudioSourceData; assetUuid: string; isAudio: boolean } | null {
    const mediaData = MediaRegistry.getInstance().getItem(mediaId);
    const background = mediaData?.background;
    const videoUuid = background?.video?.uuid;
    if (background && videoUuid && slideBackgroundModelIsVideoBackgroundModel(background)) {
        const flattenedAsset = convertBackgroundAssetToFlattenedAsset(background);
        if (
            flattenedAssetIsVideo(flattenedAsset) &&
            isFlattenedAssetMediaSourceDataCompatible(flattenedAsset)
        ) {
            return { sourceData: flattenedAsset, assetUuid: videoUuid, isAudio: false };
        }
        return null;
    }

    // An audio recording (e.g. a podcast): the clip previews become voiceover
    // scene groups — the build operation branches on the source data's shape.
    const audioAsset = VoiceoverRegistry.getInstance().getItem(mediaId)?.audio;
    if (audioAsset?.uuid && audioAsset.has_voiceover) {
        return {
            sourceData: buildAudioSourceData(audioAsset),
            assetUuid: audioAsset.uuid,
            isAudio: true,
        };
    }
    return null;
}
